import React, { useCallback, useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { colors, constants, useHover } from '../../../core';
import Button from '../Button';
import Heading from '../Heading';
import Icon from '../Icon';
import Label from '../Label';
import LinkButton from '../LinkButton';
import Text from '../Text';

type SizeType = 'xsmall' | 'small' | 'medium' | 'large';
type VariantType = 'primary' | 'secondary';
type DropdownType = 'button' | 'input' | 'icon' | 'linkButton';
type PositionType = 'left' | 'right';

export interface DropdownProps {
  options: Option[];
  heading?: string;
  width?: string;
  onChange: (option: Option) => void;
  selected: Option;
  as?: DropdownType;
  buttonText?: string;
  label?: string;
  size?: SizeType;
  variant?: VariantType;
  icon?: React.ReactNode;
  showOnHover?: boolean;
  position?: PositionType;
}

export interface Option {
  value: string;
  label: string;
}

const DropDownContainer = styled.div<{ width?: string }>`
  position: relative;
  width: ${({ width }) => (width ? width : '100%')};
`;

const DropDownHeader = styled.div<{ variant?: VariantType }>`
  display: flex;
  align-items: center;

  overflow: hidden;

  &:hover {
    p {
      text-decoration: underline;
    }
  }

  ${({ variant, theme }) =>
    variant === 'secondary' &&
    `
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    background-color: ${colors.neutral100};
    border-radius: ${theme.borderRadius || '3px'};
    padding:  ${theme.spacing.xsmall} ${theme.spacing.small};
    cursor: pointer;

    &:hover {
      p {
        text-decoration: none;
      }
    }
  `}
`;

const DropDownLabel = styled(Text)<{ variant: VariantType; width?: string }>`
  font-weight: bold;
  margin-bottom: 0;
  padding-left: 0;
  cursor: pointer;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ width }) =>
    width &&
    `
      width: calc(${width} - 60px);
    `}

  ${({ variant }) =>
    variant === 'primary' &&
    `
    padding: 0 6px;
  `}

  ${({ variant }) =>
    variant === 'secondary' &&
    `
      font-weight: 500;
    `}
`;

const DropDownHeaderLabel = styled(Label)<{ variant: VariantType }>`
  font-weight: bold;
  margin-bottom: 0;
  padding-left: 0;
  cursor: pointer;
  font-weight: 500;
  color: ${({ theme }) => theme.fontColorSecondary || colors.charcoal};
  ${({ variant }) =>
    variant === 'primary' &&
    `
      padding: 0 6px;
    `}
`;

const DropDownListContainer = styled.div<{
  showHeading?: boolean;
  type?: DropdownType;
  width?: string;
  position?: PositionType;
}>`
  position: absolute;
  z-index: 1;
  min-width: 200px;
  width: ${({ width }) => width || 'auto'};
  top: 100%;
  padding: ${({ showHeading }) => (!showHeading ? '8px 0' : '20px 0 8px')};
  background-color: ${colors.white};
  border: 1px solid rgba(221, 221, 221, 0.4);
  border-radius: ${({ theme }) => theme.borderRadius || '4px'};
  box-shadow: 0 2px 6px 0 rgba(233, 233, 233, 0.5);

  ${({ position }) => {
    switch (position) {
      case 'right':
        return `
          right: 0;
        `;
      default:
        return `
          left: 0;
          right: 0;
        `;
    }
  }}
`;

const DropDownList = styled.ul`
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  text-align: left;
`;

const ListItem = styled.li`
  list-style: none;
  padding: ${({ theme }) => theme.dropdownListItemPadding || '8px 20px'};
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.dropdownListItemBackgroundColor || colors.pearl};
  }
`;

const ListItemText = styled(Text)`
  margin-bottom: 0;
  padding: 0;
`;

const ListHeading = styled(Heading)`
  padding: 0 20px;
`;

const StyledIcon = styled(Icon).attrs(({ theme }) => ({
  color: theme.dropdownIconColor || colors.primary,
}))<{ open: boolean }>`
  cursor: pointer;
  transform: ${({ open }) => (open ? 'rotate(-180deg)' : 'rotate(0)')};
  transition: 0.3s;
`;

const Row = styled.div<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  ${({ variant, theme }) =>
    variant === 'secondary' &&
    `
    justify-content: space-between;
    gap: ${theme.spacing.xsmall || '8px'};
  `}
`;

const Column = styled.div<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  flex-direction: row;

  ${({ variant }) =>
    variant === 'secondary' &&
    `
    flex-direction: column;
    align-items: stretch;
`}
`;

const IconColumn = styled(Column)<{ variant?: 'primary' | 'secondary' }>`
  ${StyledIcon} {
    margin-left: ${({ variant }) => (variant === 'primary' ? '8px' : 0)};
  }

  ${({ variant, theme }) =>
    variant === 'secondary' &&
    `
       align-items: center;
       justify-content: center;
       gap: ${theme.spacing.xsmall || '8px'};
    `}
`;

const Dropdown = ({
  options,
  heading,
  width,
  onChange,
  selected,
  as = 'input',
  size = 'small',
  buttonText,
  label,
  variant = 'primary',
  icon,
  showOnHover = false,
  position = 'left',
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const [ref, hovering] = useHover();

  const theme = useTheme();

  const toggling = (e: any) => {
    e.stopPropagation();
    (!showOnHover || constants.IS_TOUCH_DEVICE) && setIsOpen(!isOpen);
  };

  const handleListClick = (e: any) => {
    e.stopPropagation();
  };

  const onOptionClicked = (option: Option) => () => {
    setIsOpen(false);
    onChange(option);
  };

  const handleEscape = useCallback((keyboardEvent) => {
    if (keyboardEvent.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  const handleClickOutside = useCallback((clickEvent) => {
    // @ts-ignore
    if (ref && ref.current && !ref.current.contains(clickEvent.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keyup', handleEscape);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keyup', handleEscape);
    };
  }, [handleClickOutside, handleEscape]);

  return (
    // @ts-ignore
    <DropDownContainer ref={ref} width={width}>
      {as === 'button' && (
        <Button variant={variant} size={size} onClick={(e: any) => toggling(e)} width={width}>
          {buttonText || ''}
        </Button>
      )}

      {as === 'linkButton' && (
        <LinkButton size={size} onClick={(e: any) => toggling(e)} color={theme.fontColorSecondary}>
          <Row variant={variant}>
            {icon}
            {buttonText || ''}
          </Row>
        </LinkButton>
      )}

      {as === 'icon' && (
        <DropDownHeader onClick={(e: any) => toggling(e)} variant={variant}>
          <IconColumn variant={variant}>
            {icon}
            <StyledIcon
              size={20}
              viewSize={theme.dropdownIcon.viewSize || 20}
              type={theme.dropdownIcon.type || 'caret-down'}
              open={isOpen}
            />
          </IconColumn>
        </DropDownHeader>
      )}

      {as === 'input' && (
        <DropDownHeader onClick={(e: any) => toggling(e)} variant={variant}>
          <Row variant={variant}>
            <Column variant={variant}>
              <Row variant={variant}>
                {icon}
                <Column variant={variant}>
                  {!!label && (
                    <DropDownHeaderLabel size={size} variant={variant}>
                      {label}
                    </DropDownHeaderLabel>
                  )}
                  <DropDownLabel size={size} variant={variant} width={width}>
                    {selected.label}
                  </DropDownLabel>
                </Column>
              </Row>
            </Column>
            <div>
              <StyledIcon
                size={20}
                viewSize={theme.dropdownIcon.viewSize || 20}
                type={theme.dropdownIcon.type || 'caret-down'}
                open={isOpen}
              />
            </div>
          </Row>
        </DropDownHeader>
      )}

      {(isOpen || (showOnHover && hovering && !constants.IS_TOUCH_DEVICE)) && (
        <DropDownListContainer showHeading={!!heading} type={as} width={width} position={position}>
          {heading ? <ListHeading smallCaps>{heading}</ListHeading> : null}

          <DropDownList onClick={(e: any) => handleListClick(e)}>
            {options.map((option) => (
              <ListItem onClick={onOptionClicked(option)} key={option.value}>
                {option.value === selected.value ? (
                  <ListItemText size={size}>
                    <b>{option.label}</b>{' '}
                  </ListItemText>
                ) : (
                  <ListItemText size={size}>{option.label}</ListItemText>
                )}
              </ListItem>
            ))}
          </DropDownList>
        </DropDownListContainer>
      )}
    </DropDownContainer>
  );
};
export default Dropdown;
